import type { Capabilities } from '../generated/api/capabilities.js';

export enum CommonErrorType {
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  FORBIDDEN = 'FORBIDDEN',
  SYSTEM = 'SYSTEM',
  BAD_REQUEST = 'BAD_REQUEST',
  UNPROCESSABLE_ENTITY = 'UNPROCESSABLE_ENTITY',
}

export enum CreditDecision {
  AcceptedDeposit = 'ACCEPTED_DEPOSIT',
  AcceptedInternal = 'ACCEPTED_INTERNAL',
  Accepted = 'ACCEPTED',
  DeniedBlacklist = 'DENIED_BLACKLIST',
  DeniedCreditLimit = 'DENIED_CREDIT_LIMIT',
  DeniedExternal = 'DENIED_EXTERNAL',
  DeniedInternal = 'DENIED_INTERNAL',
  DeniedCompanyRegistrationPending = 'DENIED_COMPANY_REGISTRATION_PENDING',
  Error = 'INTERNAL_ERROR',
}

export enum DeviceChangeOption {
  REDEEM = 'REDEEM',
  RETURN = 'RETURN',
  NONE = 'NONE',
}

export enum DeviceSelectionCategoryCoCo {
  PHONES = 'PHONES',
  TABLETS = 'TABLETS',
  COMPUTERS = 'COMPUTERS',
  OTHER_DEVICES = 'OTHER_DEVICES',
  ACCESSORIES = 'ACCESSORIES',
  ACCESSORIES_AND_OTHER_DEVICES = 'ACCESSORIES_AND_OTHER_DEVICES',
}

export enum DeviceSelectionProductTypeCoCo {
  EPP = 'EPP',
  PURCHASED = 'PURCHASED',
}

export enum DeviceListStatusCoCo {
  PUBLISHED = 'Published',
  DRAFT = 'Draft',
  DEPRECATED = 'Deprecated',
}

// Value of the X-Product-List-Action header, which decides whether an employee product list is saved as a draft or
// published. The API defaults to SAVE_DRAFT without the header.
export enum DeviceListActionCoCo {
  SAVE_DRAFT = 'SAVE_DRAFT',
  PUBLISH = 'PUBLISH',
}

export const PRODUCT_LIST_ACTION_HEADER = 'X-Product-List-Action';

export enum DeviceVariantProductTypeCoCo {
  Device = 'Device',
  EPP = 'EPP',
}

export enum ShoppingBasketCoCoScope {
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
}

export type PaymentType = 'BILLING_ACCOUNT' | 'CARD';

export const PaymentTypeEnum = {
  BILLING_ACCOUNT: 'BILLING_ACCOUNT' as PaymentType,
  CARD: 'CARD' as PaymentType,
} as const;

export enum DialogType {
  ADD_CONTACT = 'ADD_CONTACT',
  ASK_ABOUT_INVOICE = 'ASK_ABOUT_INVOICE',
  DAY_PICKER_DIALOG = 'DAY_PICKER_DIALOG',
  DELETE_CATALOG_DIALOG = 'DELETE_CATALOG_DIALOG',
  DUPLICATE_CONTACT = 'DUPLICATE_CONTACT',
  CONFLICTING_CONTACT = 'CONFLICTING_CONTACT',
  DUPLICATE_CONTACTS_CHECK = 'DUPLICATE_CONTACTS_CHECK',
  DUPLICATE_CONTACT_INFORMATION = 'DUPLICATE_CONTACT_INFORMATION',
  POSSIBLE_DUPLICATE_CONTACT_INFORMATION = 'POSSIBLE_DUPLICATE_CONTACT_INFORMATION',
  GENERIC_CONFIRMATION_DIALOG = 'GENERIC_CONFIRMATION_DIALOG',
  GENERIC_INFO_DIALOG = 'GENERIC_INFO_DIALOG',
  ORDER_SIM_CARD_NUMBER_HELP = 'ORDER_SIM_CARD_NUMBER_HELP',
  REVIEW_ONLINE_ORDER = 'REVIEW_ONLINE_ORDER',
  CHANGE_SUBSCRIPTION_CONTACT = 'CHANGE_SUBSCRIPTION_CONTACT',
  CHANGE_NETTI_LITE_SUBSCRIPTION = 'CHANGE_NETTI_LITE_SUBSCRIPTION',
  UPDATE_LIIKKUVA_WIFI_SUBSCRIPTION = 'UPDATE_LIIKKUVA_WIFI_SUBSCRIPTION',
  SUBSCRIPTION_SUPPORT_REQUEST = 'SUBSCRIPTION_SUPPORT_REQUEST',
  SUBSCRIPTION_SUBMIT_SUPPORT_CASE = 'SUBSCRIPTION_SUPPORT_CASE',
  SUBSCRIPTION_RESUME_FROM_TEMPORARILY_CLOSE = 'SUBSCRIPTION_RESUME_FROM_TEMPORARILY_CLOSE',
  SUBSCRIPTION_TERMINATION_FORBIDDEN = 'SUBSCRIPTION_TERMINATION_FORBIDDEN',
  SUBSCRIPTION_ACTION_PENDING = 'SUBSCRIPTION_ACTION_PENDING',
  MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION = 'MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION',
  SUBSCRIPTION_CHANGE_SPECIAL_NUMBER = 'SUBSCRIPTION_CHANGE_SPECIAL_NUMBER',
  RING_SUBSCRIPTION_UPDATE_USER = 'RING_SUBSCRIPTION_UPDATE_USER',
  SELECT_USERNAME = 'SELECT_USERNAME',
  SUBMIT_ORDER = 'SUBMIT_ORDER',
  ADMIN_FORBIDDEN = 'ADMIN_FORBIDDEN',
  EXTERNAL_AUTHENTICATION_FAIL = 'EXTERNAL_AUTHENTICATION_FAIL',
  CHANGE_SIM_CARD_CONFIRMATION = 'CHANGE_SIM_CARD_CONFIRMATION',
  UPSERT_CONTACT = 'UPSERT_CONTACT',
  UPDATE_CONTACT_DUPLICATE = 'UPDATE_CONTACT_DUPLICATE',
  UPDATE_USER_YTT_WARNING = 'UPDATE_USER_YTT_WARNING',
  ONBOARD_EMPLOYEE = 'ONBOARD_EMPLOYEE',
  BANK_BUTTONS = 'BANK_BUTTONS',
  EPP_DEVICE_CHANGE = 'EPP_DEVICE_CHANGE',
  EPP_REDEEM = 'EPP_REDEEM',
  EPP_REDEEM_REJECT = 'EPP_REDEEM_REJECT',
  EPP_TERMINATE = 'EPP_TERMINATE',
  SELECT_PRIMARY_COMPANY = 'SELECT_PRIMARY_COMPANY',
  MISSING_MOBILE_NUMBER = 'MISSING_MOBILE_NUMBER',
  NEW_RING_USERNAME = 'NEW_RING_USERNAME',
  SWITCH_ACCOUNT = 'SWITCH_ACCOUNT',
  DETACH_RING = 'DETACH_RING',
  COPY_CATALOG_MULTI_BUSINESS = 'COPY_CATALOG_MULTI_BUSINESS',
  SENDING_ONBOARDING_REQUEST = 'SENDING_ONBOARDING_REQUEST',
  BB_SUB_EMPLOYEE_INVITATION = 'BB_SUB_EMPLOYEE_INVITATION',
  BB_SUB_EMPLOYEE_NOT_FOUND = 'BB_SUB_EMPLOYEE_NOT_FOUND',
  SUPPORT_CASE_SENT_INFO_DIALOG = 'SUPPORT_CASE_SENT_INFO_DIALOG',
  USER_RIGHTS_IN_SALESFORCE = 'USER_RIGHTS_IN_SALESFORCE',
  MOVE_PAYMENT_DATE = 'MOVE_PAYMENT_DATE',
  GENERIC_EMPTY_DIALOG = 'GENERIC_EMPTY_DIALOG',
  MFA_REQUIRED_PHONE = 'MFA_REQUIRED_PHONE',
  NEW_INTERWORKS_ACCOUNT_DIALOG = 'NEW_INTERWORKS_ACCOUNT_DIALOG',
}

// In order to get test data for all of these, these GUIDS are also repeated in:
// /test-data/src/main/resources/uiapi/ui-api-test-data-paths.yaml
export enum ModelType {
  MobileBroadband = '02903874-007d-29ad-d3ea-9eb5924ba8bf',
  MobileM2M = '9b86020b-b6d7-0658-5bc6-f13fba724643',
  Ring = '9e7bf5da-c7c2-484c-c95b-5fc60d0f5011',
  Voice = '3365478d-9bc5-4efb-206c-19ed3b4b2803',
  VoicePerusliittyma4g = 'b4ff3f8d-a687-bc07-52b4-1c9fab4e6fad',
  VoiceYrityspuhe = 'f7f899e2-c489-a59f-f2a0-cfd6f320fcb7',
  Yritystupla = 'f563e704-decf-4a6c-b1a3-144a254dac27',
  VoiceSME = '79fb1a8a-27ce-4dc3-b945-ab6d3c5b765f',
  Yrityspaketti = '5c40499d-cd17-5b6a-e861-08427765f5d7',
  ElisaNetti = '8e471e61-6b00-04c2-ab3f-28686f0ab9c4',
  YrityspuheUusi = 'c6d82fa1-b696-052d-70f0-169f4096d151',
}

/** Virtual model types that don't correspond to real backend GUIDs. Used for internal routing only. */
export enum VirtualModelType {
  LiikkuvaWifi = 'liikkuva-wifi',
}

/** Union of real and virtual model types. Use this where virtual types like LiikkuvaWifi are also accepted. */
export type OnlineModelType = ModelType | VirtualModelType;

export enum PhoneNumberOwnerType {
  PERSON = 'PERSON',
  COMPANY = 'COMPANY',
}

export enum PhoneNumberType {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
}

export enum ReduxStateEnum {
  FULFILLED = 'FULFILLED',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
}

export enum SelectedPurposeOfUseOrContact {
  CONTACT = 'CONTACT',
  PURPOSEOFUSE = 'PURPOSEOFUSE',
}

export enum SimCardSelection {
  ORDER_NEW = 'ORDER_NEW',
  ACTIVATE_EXISTING = 'ACTIVATE_EXISTING',
}

export enum SubscriptionCategory {
  BROADBAND = 'broadband',
  VOICE = 'voice',
  DEVICE = 'device',
  SUBSCRIPTIONS = 'subscriptions',
  SERVICE = 'service',
  DOMAIN = 'domain',
  CONTRACT = 'contract',
  LICENSES = 'licenses',
}

export enum SubscriptionFilterValue {
  ALL = 'All',
  MOBILE_SUBSCRIPTIONS = 'Mobile subscriptions',
  FIXED_LINE_SUBSCRIPTIONS = 'Fixed line subscriptions',
  FIXED_BROADBAND = 'Fixed broadband',
  MOBILE_BROADBANDS = 'Mobile broadbands',
  PHONE = 'Phone',
  TABLET = 'Tablet',
  COMPUTERS = 'Computers',
  ACCESSORIES = 'Accessories',
  NETWORK_EQUIPMENT = 'Network equipment',
  OTHERS = 'Elisa Yritystietoturva,CPE,Elisa Yritysgurun asiantuntijapalvelut',
}

export enum SubscriptionDeviceEppFilter {
  All = 'All',
  BasicContractPeriod = 'BasicContractPeriod',
  ExtensionPeriod = 'ExtensionPeriod',
}

export enum SubscriptionDeviceExpiringFilter {
  ExpiringIn2Weeks = 'ExpiringIn2Weeks',
}

export enum SubscriptionDevicePaymentTypeFilter {
  Onetime = 'Onetime',
  Monthly = 'Monthly',
}

export enum SubscriptionDeviceCommitmentPeriodFilter {
  Period12Months = '12',
  Period24Months = '24',
  Period36Months = '36',
  Period48Months = '48',
}

export enum SubscriptionDeviceUserFilter {
  Shared = 'Shared',
  Personal = 'Personal',
}

export enum SupportCaseFields {
  SUPPORT_CASE_DISPLAY_ID = 'supportCaseDisplayId',
  CREATED_AT = 'createdAt',
  FEATURE = 'feature',
  CONTACT_DETAILS = 'contactDetails',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  LAST_MODIFIED = 'lastModified',
  STATUS = 'status',
  FEATURE_TYPE = 'featureType',
  INVOICE_NUMBER = 'invoiceNumber',
  DESCRIPTION = 'description',
  RESOLUTION = 'resolution',
  COMPANY = 'company',
}

export enum SwitchAccountJourneyPoint {
  ONBOARDING = 'ONBOARDING',
  MOBILE_SUB_PAIRING = 'MOBILE_SUB_PAIRING',
}

export enum TerminateType {
  ALL = 'ALL',
  VOICE = 'VOICE',
  RING = 'RING',
  RING_CONNECTION = 'RING_CONNECTION',
}

export enum WizardType {
  NEW_MOBILE_BROADBAND = 'NEW_MOBILE_BROADBAND',
}

export enum CustomerOrderListFields {
  CUSTOMER_ORDER_DISPLAY_ID = 'customerOrderDisplayId',
  CREATED = 'created',
  DELIVERY_ORDER_CONTACT_NAMES = 'deliveryOrderContactNames',
  DELIVERY_ORDERS = 'deliveryOrders',
  STATUS = 'status',
  COMPANY = 'company',
  TOPIC = 'topic',
}

export enum CocoOrdersStatusFilterValues {
  PENDING = 'pending',
  PROCESSING = 'processing',
  PARTIALLY_DELIVERED = 'partiallyDelivered',
  DELIVERED = 'delivered',
  OPEN = 'open',
  CANCELLED = 'cancelled',
  SCHEDULED = 'scheduled',
}

export enum CocoOrdersProductTypeFilterValues {
  MOBILE = 'mobileOffer',
  BROADBAND = 'businessInternet',
  EPP = 'EPP Device',
  DEVICE = 'Device',
  SERVICE = 'SoftwareProduct',
  OTHER = 'other',
}

export enum OrderConfirmationListFields {
  CUSTOMER_ORDER_DISPLAY_ID = 'customerOrderDisplayId',
  CREATED = 'created',
  DELIVERY_ORDERS = 'deliveryOrders',
  CUSTOMER_ORDER_CONTACT = 'customerOrderContact',
  COMPANY = 'company',
  DOWNLOAD_ORDER_CONFIRMATION = 'downloadOrderConfirmation',
}

export enum CocoOrderType {
  NEW_ORDER = 'NEW_ORDER',
  CHANGE_ORDER = 'CHANGE_ORDER',
}

export enum CoCoOrderAction {
  ADD = 'add',
  CHANGE_OWNERSHIP = 'change_ownership',
  RESUME = 'resume',
  TERMINATE = 'terminate',
  SUSPEND = 'suspend',
  REPLACE_OFFER = 'replace_offer',
}

export enum WarrantyReceiptListFields {
  SUBSCRIPTION_NAME = 'subscriptionName',
  CREATED = 'created',
  SUBSCRIPTION_DISPLAY_ID = 'subscriptionDisplayId',
  SUBSCRIPTION_CONTACT_NAME = 'subscriptionContactName',
  COMPANY_NAME = 'companyName',
  DOWNLOAD_WARRANTY_RECEIPT = 'downloadWarrantyReceipt',
}

export enum ContactListFields {
  CONTACT_NAME = 'name',
  CONTACT_FIRST_NAME = 'firstName',
  CONTACT_LAST_NAME = 'lastName',
  CONTACT_PHONE_NUMBER = 'phoneNumber',
  CONTACT_EMAIL = 'email',
  CONTACT_EOE_IN_USE = 'eoeInUse',
  COMPANY = 'company',
}

export enum CallerDisplayNameListFields {
  SUBSCRIPTION_LINK = 'subscriptionLink',
  PHONE_NUMBER = 'phoneNumber',
  ACTIVE_CALL_DISPLAY = 'activeDisplayName',
  DIRECTORY_INFO = 'originalDisplayName',
  CALLER_DISPLAY_NAME = 'displayUser',
  COMPANY_DISPLAY_NAME = 'displayCompany',
}

export enum SubscriptionAdditionalServiceStatusEnum {
  IN_USE = 'IN_USE',
  NOT_IN_USE = 'NOT_IN_USE',
  CHANGE_REQUESTED = 'CHANGE_REQUESTED',
}

export enum AddOnRulesSubscriptionType {
  ELISA_NETTI = 'ELISA_NETTI',
  ELISA_NETTI_4G = 'ELISA_NETTI_4G',
}

export enum SubscriptionAddOnGroup {
  PUBLIC_IP = 'Public Dynamic IP',
  FIXED_PUBLIC_IP = 'Kiinteä IP - Vain MOR-tiimi',
}

export enum SubscriptionAddonCode {
  VOICE_MAIL = '463ba484-042f-799c-f706-7f9cce737580',
  MOBIILITURVA = '6744cb06-0bf8-0f6c-9cf6-da4d91d39d3e',
  REISSUNETTI1 = '721120d8-4ae7-bcd9-9786-888ab5549e1e',
  REISSUNETTI2 = '8ded730b-aa92-6651-38f6-dd71a0c71dc1',
  REISSAAJA = '95eb2c18-55fc-47c4-9993-5b7688f7bf32',
  PUBLIC_DYNAMIC_IP = '8bce627a-83b4-d62f-f429-be64b57edf36',
  PUHELUN_EDELLEENYHDISTAMINEN = 'fe2c9c53-ec86-d774-0ba2-d6c70b63ca87',
  YRITYS_WIFI_LITE = '6db0d5a4-8065-20be-69cd-20c82b429391',
  REACHABILITY_WITH_MOBILE = '9c859d06-cf39-29b9-f596-2d33ae61a3c2',
  VAKIO_CORPORATE_NUMBER = 'a1865e36-d078-8c3b-6ccf-99479033a46a',
  VAKIO_REGIONAL_NUMBER = 'f9daa8c0-97f9-adf0-b653-2bf60fa9342d',
  KUKA_SOITTAA = '27df8d81-b6f4-0aeb-b0fe-0a841a4622d0',
  KUKA_SOITTAA_PLUS = '9b6ab315-a781-9b77-c3ab-d5bc64b9ede7',
  MOBIILIVARMENNE = '95fd03f5-d93d-9ff5-37a8-5be322a1b182',
}

export enum AddOnVisibilityOperationType {
  NEW_SALES = 'NEW_SALES',
  CHANGE = 'CHANGE',
}

export enum ElisaYrityspuheUusiOfferCode {
  VoiceYrityspuhePremium = 'eaa24a1a-80a7-1a29-4c77-da2eecb420dd',
  VoiceYrityspuhePro1000M = 'fde78553-311c-8239-0718-f599174acf06',
  VoiceYrityspuhePro600M = 'c38a21d4-96a3-2aa6-8032-0a20ebc8a900',
  VoiceYrityspuhePlus300M = '83a28bc9-d9aa-8563-a3ae-3daf0013492d',
  VoiceYrityspuhePlus100M = 'b022bcef-e4d9-307a-83c3-48b5ff76eb4b',
  VoiceYrityspuheBasic = '2ad99c90-932d-1400-7886-323ed5316e42',
  VoiceYrityspuheLite300M = 'e005bf73-9843-20c4-7cfa-957618c2eb56',
  VoiceYrityspuheLite100M = '34e83c5c-4071-5de6-4c10-bca1a1acedc6',
  VoiceYrityspuheLite50M = 'df1b22b8-1845-89b0-36e2-25473c980bb3',
}

export enum FiveGVoiceOfferCode {
  Voice5gPlus1000Mbps = 'f31d8831-149b-4519-91f2-c294d7fafb2f',
  Voice5gPlus600Mbps = '1c8e135b-f46d-dc5f-75e5-727077eec8d3',
  Voice5gPlus300Mbps = '76d463e0-a759-b05b-e775-c6af2144f7d5',
  Voice1000Mbps = '21782f1a-f344-5e27-7c9e-ab609c846de1',
  Voice600Mbps = 'fd552b9f-7806-3ab5-92f6-a26101fe1833',
  Voice300Mbps = '687b1fd4-c1ce-2b7d-398b-18ac9c941a0d',
  Voice50Mbps = 'b03eb93b-5b89-63d2-e5c1-20c9cf78a2ec',
  Voice10Mbps = '6fe27af9-0139-dcd9-317f-4c78b9259ca0',
}

export enum FiveGVoiceProductCode {
  Voice5gPlus1000Mbps = '48086af1-ab14-3d9a-467d-f364008c5425',
  Voice5gPlus600Mbps = '1ebd5523-d223-8eff-6b6a-411d975fbfb8',
  Voice5gPlus300Mbps = '634541fd-a81d-ea21-da1e-213b64575812',
  Voice1000Mbps = '095578d5-1e7b-7afb-50b5-e6dad4c75dbb',
  Voice600Mbps = '4cf307b5-f8e6-497d-7a7e-2ec6ff9ff12c',
  Voice300Mbps = 'a1db43db-b9e7-892e-aa17-a8f631fdd54f',
  Voice50Mbps = '984d1307-f2ca-0d02-3307-f37097f46189',
  Voice10Mbps = '02f9e0c8-b4f2-dfb9-7992-f6d85a6954ef',
}

export enum VoiceYrityspuheLiteProductCode {
  VoiceYrityspuheLite300M = 'b6823a79-a461-6818-f07f-7183703393e2',
  VoiceYrityspuheLite100M = '5899d5a8-9595-33d5-5286-5005db6691d8',
  VoiceYrityspuheLite50M = '2ff4fa6e-9b74-f0fa-2251-bc8f7d886976',
}

export enum MobileBroadbandFourGOfferCodes {
  ONE_FIFTY = '942378bd-dbdd-a8a2-9c07-e5556ae760f2',
  FIFTY = '6655bafe-4eb5-76c8-21e5-bcc3f3053ceb',
  TEN = '67ecc03c-3b77-9da6-3fa7-8c27b3b11a53',
}

export enum MobileBroadbandFourGProductCodes {
  ONE_FIFTY = 'ecadb593-5a17-740e-6735-51f0a4de4d34',
  FIFTY = '571f91d0-8280-238f-f751-d588e5e0b86f',
  TEN = 'b557e5a3-63f5-a697-0ef9-c60fc8785b4c',
}

export enum OldVoiceOfferCode {
  VOICE_1000_M = 'a1e13276-caf3-7c8b-709c-4026bdc04bed',
  VOICE_600_M = '945c1ec9-a53b-8f09-b330-0cf033bf7dba',
  VOICE_300_M = '0f6a0350-daf4-854d-f0b9-b532df78eb51',
  VOICE_100_M = '0dd20ed5-51ce-ff8c-16ac-582599416b0f',
  VOICE_10_M = '96afd01c-4573-660a-cad0-9a42197d14c2',
}

export enum OldVoiceProductCode {
  VOICE_1000_M = '91e84b06-0061-7c6e-06d1-40223eceafba',
  VOICE_600_M = 'cad3abbc-4f28-21e6-79d4-c9baa73c1d8e',
  VOICE_300_M = 'f08aa0b1-447a-a00f-ad4e-e78416c6e67d',
  VOICE_100_M = '9c4633b8-50c0-20c9-446d-584c7488620c',
  VOICE_10_M = '0a004191-be2f-03ec-e69c-5bac1549de71',
}

export enum YrityspakettiOfferCode {
  VOICE_1000_M = 'db272d85-47a6-1cec-56fc-596ad6ce6ba1',
  VOICE_600_M = '06337470-6860-bdd8-b3eb-6bcb1a61fc81',
  VOICE_300_M = 'e9c207e1-4497-6249-c6e7-11109b153fb9',
  VOICE_200_M = 'bbf713d0-7b75-6ad3-9f3e-5213fcb3fb3c',
  VOICE_100_M = 'd92d099a-5139-4427-19ba-087a18595d69',
  VOICE_50_M = '2ee43890-0273-fc9c-712a-604b2bbe12d7',
  VOICE_4G_50_M = '1105d565-ce44-ba45-4c94-3b8c27fffdce',
  VOICE_10_M = '603d1fc6-9fb5-2d25-63be-a6db49e6d9f8',
  VOICE_2_M = 'b3d9af15-9589-26fd-93a1-f6cc067288d3',
}

export enum YrityspakettiProductCode {
  VOICE_1000_M = '229b8725-fce3-aa76-6f0f-26ec1534523f',
  VOICE_600_M = 'c2b7915e-d129-6362-6799-d6df9ada504d',
  VOICE_300_M = '1f8af0de-61f7-b551-550e-6b12c9b9f666',
  VOICE_200_M = '97714b7d-a484-2411-f91c-d09bbf88af9f',
  VOICE_100_M = 'e7bf981c-d3ce-39f1-2d50-fc2c0c3d53e8',
  VOICE_50_M = 'd6e9a29a-8071-0867-0fdd-2f079f478a04',
  VOICE_4G_50_M = '64e7c733-7a42-c483-3a36-81838e5f019c',
  VOICE_10_M = '6f1fb804-c51c-e2e2-5038-6d12198947ac',
  VOICE_2_M = '5dad152a-743b-af36-db94-fe8856b4832f',
}

export enum FiveGMobileBroadbandOfferCode {
  MBB_5G_PLUS_1000_M = '5230ad2e-3f96-8e8f-2de3-bc78e3ecb250',
  MBB_5G_PLUS_600_M = '4e2a5022-12e2-3b01-3ab6-2c9ae42cab6f',
  MBB_5G_PLUS_300_M = 'a66bd6ea-d023-9259-5e5c-30034a7e86c0',
  MBB_5G_PLUS_100_M = '78bc7361-8f48-2008-c273-c8a41ebb4ca2',
  MBB_1000_M = '7797909c-3f7e-b27d-e159-96124f0cbbbb',
  MBB_600_M = '83e3965c-e84a-0c8a-18c1-6d04171951f5',
  MBB_300_M = 'd57895b4-abe3-6d85-278c-1c771aecce9c',
  MBB_100_M = '026acb30-8f07-845b-6eb3-42e8d3bbef22',
  MBB_50_M = '2f2f537b-749a-486f-e2f5-3b129e51bd9d',
  MBB_10_M = '2fd89894-10cf-65a9-ff49-33dfdb232ac4',
}

export enum FiveGMobileBroadbandProductCode {
  MBB_5G_PLUS_1000_M = '77c94d90-1b39-c358-90e7-19b6e1f8d2da',
  MBB_5G_PLUS_600_M = '2633979f-9e5d-40ca-aaa6-dfc4dd59617a',
  MBB_5G_PLUS_300_M = '916277d7-e8ef-084b-df31-4e18dc235443',
  MBB_5G_PLUS_100_M = '26be32d9-d04d-c857-12e5-4e1ac01bf920',
  MBB_1000_M = 'f551f58c-ff74-ea5b-9ed9-05c8e755dc41',
  MBB_600_M = 'aed0c1c7-d8b6-44d9-0cb4-19a7019b282d',
  MBB_300_M = '71c16121-bd84-97f2-0ecc-9eb438b74189',
  MBB_100_M = 'b5578871-ac22-0d65-6358-81dbf9995cb1',
  MBB_50_M = '1f98c558-ea32-4592-036e-28daa73746a4',
  MBB_10_M = '0aea398d-b206-7980-4a32-889b9030315d',
}

export enum MobileM2MAddonCode {
  S = '06c6d401-3f57-f27f-4726-5b9d0edd84bb',
  M = '21127eef-7290-1d21-bf10-02d608e96adf',
  L = 'a22f20d3-0ba4-d874-22d6-dbf71627a6d0',
  fiveG = '497aa153-9530-0665-abaa-3fd0224d2c00',
}

export enum SubscriptionCardType {
  VOICE = 'VOICE',
  MOBILE_BROADBAND = 'MOBILE_BROADBAND',
  MOBILE_M2M = 'MOBILE_M2M',
}

export enum MobiilivarmenneAddonGroups {
  CERTIFICATE = 'Certificate',
}

export enum OmalaskuBarringFields {
  SUBSCRIPTION_USER_FRIENDLY_ID = 'subscriptionUserFriendlyId',
  BARRING_STATUS = 'barringStatus',
  BARRINGS = 'barrings',
}

export enum BillingAccountSubscriptionFields {
  NAME = 'subscriptionName',
  IDENTIFIER = 'identifier',
  MONTHLY_RECURRING_CHARGE = 'monthlyRecurringCharge',
  ONE_TIME_CHARGE = 'oneTimeCharge',
  STATUS = 'subscriptionStatus',
}

export enum StatusColor {
  DARK_BLUE = 'blue',
  GREEN = 'green',
  GREY = 'neutral',
  RED = 'red',
  SALMON = 'salmon',
  YELLOW = 'warning-subtle',
  ORANGE = 'orange',
}

// COCO related enums
export enum OfferType {
  VOICE = 'voice',
  MOBILE_BROADBAND = 'mobileBroadband',
  SOFTWARE = 'software',
  DEVICE = 'device',
  PHONES = 'phones',
  ANDROID = 'android',
  IOS = 'ios',
  ACCESSORIES = 'accessories',
  OTHER_PHONES = 'otherPhones',
  FIXED_BB = 'Netti_Lite',
}

export enum OfferCategory {
  MOBILE_DEVICE = 'mobileDevice',
}

export enum OfferSubCategory {
  MOBILE_VOICE = 'Mobile_Voice_Offer_POT',
  MOBILE_BROADBAND = 'Mobile_BB_Offer_POT',
  SOFTWARE = 'YTT_Software_Offer_POT',
  FIXED_MOBILE_BROADBAND = 'Fix_Mobile_BB_Offer_POT',
  FIXED_BROADBAND = 'Fix_BB_Offer_POT',
  DEVICE_POT = 'Device_POT',
  EPP_DEVICE_POT = 'EPP_DEVICE_POT',
  // TODO verify the values from catalog
  FIXED_BB_CABLE_MODEM = 'Fix_Cable_BB_Offer_POT',
  FIXED_BB_OMAKAISTA = 'Fix_Omakaista_Offer_POT',
  INSTALLATION_SERVICE_POT = 'Installation_Service_POT',
  INSURANCE_POT = 'Insurance_POT',
  QUANTITY_BASED_ACCESSORY = 'Quantity_Based_Accessory_POT',
}

export enum AddonSubCategory {
  MOBILE_DATA_POT = 'Mobile_Data_POT',
  CUSTOMER_OWN_CPE = 'Customer_Own_CPE_POT',
}

export enum ProductOfferCategory {
  MOBILE_OFFER = 'mobileOffer',
  SOFTWARE = 'softwareOffer',
}

export enum ProductOfferClassification {
  MAIN_OFFER = 'subscriberMainOffer',
  OFFER = 'subscriberOffer',
}

export const FIXED_BROADBAND_SUBCATEGORIES = [
  OfferSubCategory.FIXED_BROADBAND,
  OfferSubCategory.FIXED_MOBILE_BROADBAND,
] as const;

export const MAIN_USE_OF_SUBSCRIPTION = 'main_use_of_subscription';

/** Offer subcategories that represent an actual device (own or EPP). */
const DEVICE_EPP_SUBCATEGORIES = new Set<string>([OfferSubCategory.DEVICE_POT, OfferSubCategory.EPP_DEVICE_POT]);

export const isDeviceEppSubcategory = (subcategory?: string): boolean =>
  subcategory !== undefined && DEVICE_EPP_SUBCATEGORIES.has(subcategory);

export const AssetType = {
  BILLING_ACCOUNT: 'ba',
  ORDER: 'order',
  INVOICE: 'invoice',
} as const satisfies Record<string, keyof Capabilities>;

export enum RelatedPartyRoles {
  PROPOSAL_CREATOR = 'proposalCreator',
  APPROVER = 'approver',
  CUSTOMER = 'customer', // Organization
  DELIVERY_CONTACT = 'deliveryContact',
  ASSIGNEE = 'assignee', // logged in user
  USER = 'User', // user to which the order item is assigned
  INSTALLATION_CONTACT = 'installationContact', // user who will be present during product installation
}

export enum ShipmentOrderEnum {
  TOTAL = 'Total',
  PARTIAL = 'Partial',
}
